import * as React from 'react'
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { StocktakeLine } from '@/hooks/useStocktakes'

// Sig stripe (3px left-edge accent on each row), same convention as the
// receiving and dashboard tables. Stocky only marks ONE thing here: a row whose
// Adjustment is 0 gets a green indicator ("this shelf agrees with Shopify").
// Discrepancies get no stripe, so the eye lands on the rows that need a second
// look rather than on the ones that are fine. A never-counted row has no
// Adjustment at all, so it gets no stripe either — it isn't agreement.
const MATCH_GREEN = 'var(--meter-good)'

// "N held" next to Available — a muted outline chip rather than a status pill
// (docs/designs/colour-pass.md), because it is context for a Missed row, not a
// status of its own.
const HELD_CHIP =
  'inline-block whitespace-nowrap border border-[var(--line-warm)] px-1 py-px text-[10px] font-semibold leading-none text-[var(--mute)] cursor-help'

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n)
}

// Editable "Scanned" cell — the Search tab's manual-entry path, for tags that
// won't scan. Writes with mode 'set', so the typed number replaces the count
// rather than adding to it.
function ScannedCell({
  line,
  onSetCount,
  pending,
}: {
  line: StocktakeLine
  onSetCount: (line: StocktakeLine, qty: number) => void
  pending: boolean
}) {
  // While the user is mid-edit the draft wins; otherwise the cell tracks the
  // cache, so a scan that increments this line shows up straight away. A
  // never-counted line shows an EMPTY box, not a 0 — typing 0 into it has to
  // stay a real act, because it is what puts the variant into the apply.
  const [draft, setDraft] = React.useState<string | null>(null)
  const value = draft ?? (line.counted_qty == null ? '' : String(line.counted_qty))

  function commit() {
    if (draft == null) return
    const trimmed = draft.trim()
    setDraft(null)
    if (trimmed === '') return
    const qty = Number(trimmed)
    if (!Number.isInteger(qty) || qty < 0) return
    if (qty === line.counted_qty) return
    onSetCount(line, qty)
  }

  return (
    <div className="flex items-center justify-end">
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={value}
        disabled={pending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            setDraft(null)
          }
        }}
        className="w-[54px] border border-[var(--line-input)] bg-transparent px-1 py-0.5 text-right text-xs font-semibold tabular-nums text-[var(--text-primary)]"
        aria-label={`Scanned quantity for ${line.product_title}`}
        data-testid={`stocktake-count-input-${line.line_id}`}
      />
    </div>
  )
}

interface StocktakeTableProps {
  lines: StocktakeLine[]
  /** Search tab: the Scanned cell becomes an input (manual count entry). */
  editable?: boolean
  onSetCount?: (line: StocktakeLine, qty: number) => void
  /** Missed tab: per-row "Mark as 0" — the deliberate "we really don't have
   *  it" act that turns a never-counted line into a counted-empty one. */
  onMarkZero?: (line: StocktakeLine) => void
  /** Applied session: show what was actually pushed to Shopify per line. */
  showApplied?: boolean
  /** Last line touched by a scan — highlighted so the operator sees the hit. */
  highlightLineId?: number | null
  pendingLineId?: number | null
}

/** Stocky's count sheet: Product · Variant · SKU · Barcode · Available ·
 *  Scanned · Adjustment. "Available" is the baseline captured at creation and
 *  never moves; "Adjustment" is Scanned − Available. */
export function StocktakeTable({
  lines,
  editable,
  onSetCount,
  onMarkZero,
  showApplied,
  highlightLineId,
  pendingLineId,
}: StocktakeTableProps) {
  const columns = React.useMemo<ColumnDef<StocktakeLine>[]>(() => {
    const cols: ColumnDef<StocktakeLine>[] = [
      {
        id: 'sig',
        header: () => null,
        cell: ({ row }) => {
          const match = row.original.variance === 0
          return (
            <div
              className="h-[28px] w-[3px]"
              style={{ background: match ? MATCH_GREEN : 'transparent' }}
              data-testid={`stocktake-sig-${row.original.line_id}`}
              data-match={match ? 'true' : 'false'}
            />
          )
        },
        size: 4,
      },
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => (
          <span className="truncate text-xs font-medium text-[var(--text-primary)]">
            {row.original.product_title || '—'}
          </span>
        ),
      },
      {
        id: 'variant',
        header: 'Variant',
        size: 150,
        cell: ({ row }) => (
          <span className="truncate text-xs text-[var(--ink-2)]">
            {row.original.variant_title || '—'}
          </span>
        ),
      },
      {
        id: 'sku',
        header: 'SKU',
        size: 130,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-[var(--ink-2)]">
            {row.original.sku || '—'}
          </span>
        ),
      },
      {
        id: 'barcode',
        header: 'Barcode',
        size: 140,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-[var(--ink-2)]">
            {row.original.barcode || '—'}
          </span>
        ),
      },
      {
        id: 'available',
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help border-b border-dotted border-[var(--mute)]">
                Available
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-left">
              Shopify on-hand at this location, captured when the stocktake was
              created. It does not change while you count. The applied adjustment
              is recomputed against live stock.
            </TooltipContent>
          </Tooltip>
        ),
        size: 120,
        meta: { numeric: true },
        cell: ({ row }) => {
          const held = row.original.baseline_committed ?? 0
          return (
            <span className="inline-flex items-center justify-end gap-1">
              <span className="text-xs tabular-nums text-[var(--ink-2)]">
                {row.original.baseline_on_hand ?? '—'}
              </span>
              {/* Held stock is why a row can be Missed on purpose: it's boxed
                  for an exchange, not sitting on the rack to be scanned. */}
              {held > 0 && (
                <span
                  className={HELD_CHIP}
                  title="Committed to unfulfilled orders (holds/exchanges) — may be set aside off the shelf, not on the sales floor."
                  data-testid={`stocktake-held-${row.original.line_id}`}
                >
                  {held} held
                </span>
              )}
            </span>
          )
        },
      },
      {
        id: 'scanned',
        header: 'Scanned',
        size: 100,
        meta: { numeric: true },
        cell: ({ row }) => {
          const line = row.original
          if (editable && onSetCount) {
            return (
              <ScannedCell
                line={line}
                onSetCount={onSetCount}
                pending={pendingLineId === line.line_id}
              />
            )
          }
          // null is NOT 0 here: an em dash says "nobody counted this", which
          // is the difference between leaving Shopify alone and zeroing it.
          const counted = line.counted_qty
          return (
            <span
              className={cn(
                'text-xs tabular-nums',
                counted ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--mute)]'
              )}
              data-testid={`stocktake-scanned-${line.line_id}`}
            >
              {counted ?? '—'}
            </span>
          )
        },
      },
      {
        id: 'adjustment',
        header: 'Adjustment',
        size: 100,
        meta: { numeric: true },
        cell: ({ row }) => {
          const v = row.original.variance
          return (
            <span
              className={cn(
                'text-xs font-semibold tabular-nums',
                // Negative = stock we thought was there and isn't (shrinkage,
                // miscount, theft): the key signal on a count, so it stays red,
                // softened to the status red. Positive is found stock, green.
                v != null && v > 0 && 'text-[var(--status-approved-fg)]',
                v != null && v < 0 && 'text-[var(--status-rejected-fg)]',
                (v == null || v === 0) && 'font-normal text-[var(--mute)]'
              )}
              data-testid={`stocktake-variance-${row.original.line_id}`}
            >
              {v == null ? '—' : signed(v)}
            </span>
          )
        },
      },
    ]

    // Missed tab only: the deliberate "we really don't have it" act. Posts an
    // explicit 0, which moves the row onto the Scan tab and puts the variant
    // into the apply — the one thing walking past a peg does NOT do.
    if (onMarkZero) {
      cols.push({
        id: 'markzero',
        header: () => null,
        size: 110,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onMarkZero(row.original)}
            disabled={pendingLineId === row.original.line_id}
            className="border border-[var(--line-warm)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--ink-2)] hover:text-[var(--text-primary)] disabled:opacity-50"
            data-testid={`stocktake-mark-zero-${row.original.line_id}`}
          >
            Mark as 0
          </button>
        ),
      })
    }

    // Archive column: what actually went to Shopify, which is NOT the
    // Adjustment above — the delta is recomputed against live stock at apply
    // time.
    if (showApplied) {
      cols.push({
        id: 'applied',
        header: 'Applied',
        size: 90,
        meta: { numeric: true },
        cell: ({ row }) => {
          const line = row.original
          if (line.apply_status === 'failed') {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="cursor-help text-xs font-semibold text-[var(--status-rejected-fg)]"
                    data-testid={`stocktake-applied-${line.line_id}`}
                  >
                    failed
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-left">
                  {line.apply_error || 'Shopify rejected this adjustment.'}
                </TooltipContent>
              </Tooltip>
            )
          }
          return (
            <span
              className={cn(
                'text-xs tabular-nums',
                line.applied_delta ? 'font-semibold text-[var(--text-primary)]' : 'text-[var(--mute)]'
              )}
              data-testid={`stocktake-applied-${line.line_id}`}
            >
              {line.applied_delta == null ? '—' : signed(line.applied_delta)}
            </span>
          )
        },
      })
    }

    return cols
  }, [editable, onSetCount, onMarkZero, showApplied, pendingLineId])

  const table = useReactTable({
    data: lines,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex-1 overflow-auto">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            {columns.map((c) => (
              <col key={c.id} style={c.size ? { width: c.size } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {table.getHeaderGroups()[0].headers.map((header) => {
                const numeric =
                  (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                    ?.numeric ?? false
                return (
                  <th
                    key={header.id}
                    className={cn(
                      'tbl-th border-b border-[var(--line-warm)] text-left',
                      numeric && 'text-right'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.id}
                // The last-scanned row is a "you are here" marker, not an
                // attention signal, so it borrows the header tint over the zebra.
                className={cn(
                  highlightLineId === row.original.line_id
                    ? 'bg-[var(--surface-header)]'
                    : index % 2 === 1
                      ? 'bg-[var(--surface-zebra)]'
                      : 'bg-[var(--surface-card)]'
                )}
                data-testid={`stocktake-row-${row.original.line_id}`}
              >
                {row.getVisibleCells().map((cell) => {
                  const numeric =
                    (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                      ?.numeric ?? false
                  const isSig = cell.column.id === 'sig'
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        'border-b border-[var(--line-warm)] align-middle',
                        isSig ? 'p-0' : 'px-2 py-2 text-xs',
                        numeric && 'text-right tabular-nums'
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}
